import {useLocalStorage} from "@mantine/hooks";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {useCallback, useEffect, useRef, useState} from "react";
import {
    TextChangedCallback,
    typeAnimatedText,
    typeAnimatedTexts,
} from "./utils";
import {useNavigate} from "react-router-dom";
import {clearThread} from "./chatSlice";

// TODO: Probably a better way to do this but need to consult with Seb
/* export const useSelectedSystem = (): [
    string | null,
    (name: string) => void
] => {
    const dispatch = useAppDispatch();
    const system = useAppSelector((state) => state.chat.system);
    const [selectedChatSystem, setSelectedChatSystem] = useLocalStorage({
        key: "selectedChatSystem",
        defaultValue: system?.name,
    });

    useEffect(() => {
        if (selectedChatSystem) {
            dispatch(setSystem({name: selectedChatSystem}));
        }
    }, [dispatch, selectedChatSystem]);

    return [selectedChatSystem, setSelectedChatSystem];
}; */

interface AnimatedTextOptions {
    /**
     * The time in milliseconds between each update
     * Defaults to 5
     */
    delta?: number;
    /**
     * The number of characters to update at a time
     * Defaults to 3
     */
    chunkSize?: number;
}

/**
 * Animated text hook that animates text over time. Used for performant, smooth streaming
 * of text. E.g. from the LLM assistant.
 * @param text The text to animate.
 * @param options Optional configuration for the animation.
 * @returns The animated text.
 */
export const useAnimatedText = (
    text: string,
    options: AnimatedTextOptions = {}
) => {
    const {delta = 5, chunkSize = 3} = options;
    const [displayText, setDisplayText] = useState(text);
    const frameRef = useRef<number | null>(null);
    const lastTimeRef = useRef<number>(0);

    const animate = useCallback(
        (currentTime: number) => {
            if (!lastTimeRef.current) lastTimeRef.current = currentTime;

            const deltaTime = currentTime - lastTimeRef.current;

            // Update every n ms
            if (deltaTime >= delta) {
                if (displayText.length < text.length) {
                    setDisplayText(
                        text.slice(0, displayText.length + chunkSize)
                    );
                    lastTimeRef.current = currentTime;
                }
            }

            if (displayText.length < text.length) {
                frameRef.current = requestAnimationFrame(animate);
            }
        },
        [displayText, text, delta, chunkSize]
    );

    useEffect(() => {
        frameRef.current = requestAnimationFrame(animate);

        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
        };
    }, [text, animate]);

    return displayText;
};

export const useSingleShotAnimatedText = (text: string): [string, boolean] => {
    const [animatedText, setAnimatedText] = useState<string>("");
    const [isFinished, setIsFinished] = useState(false);

    useEffect(() => {
        typeAnimatedText(
            text,
            (updatedText, isFinished) => {
                setAnimatedText(updatedText);
                setIsFinished(isFinished);
            },
            {
                typingSpeed: 100,
                backspaceSpeed: 50,
                holdSpeed: 1000,
                waitSpeed: 2000,
                loop: false,
                backspaceOnFinish: false,
            }
        );
    }, [text]);

    return [animatedText, isFinished] as const;
};

export const useAnimatedTexts = (
    messages: string[],
    options: {skip?: boolean; holdSpeed?: number; waitSpeed?: number; loop?: boolean; backspaceOnFinish?: boolean} = {}
) => {
    const {skip = false, holdSpeed = 1000, waitSpeed = 2000, loop = true, backspaceOnFinish = true} = options;
    const [animatedText, setAnimatedText] = useState<string>("");

    useEffect(() => {
        if (skip) {
            return;
        }

        const onTextChanged: TextChangedCallback = (updatedText) => {
            setAnimatedText(updatedText);
        };

        typeAnimatedTexts(messages, onTextChanged, {
            typingSpeed: 100,
            backspaceSpeed: 50,
            holdSpeed: holdSpeed,
            waitSpeed: waitSpeed,
            loop: loop,
            backspaceOnFinish: backspaceOnFinish,
            preserveMarkup: true,
        }).catch((err) => {
            console.error("Animation Error:", err);
        });
    }, [skip, holdSpeed, waitSpeed, loop, backspaceOnFinish]);

    return animatedText;
};

export const useGoHome = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleClearChat = useCallback(() => {
        dispatch(clearThread());
        navigate("/");
    }, [dispatch, navigate]);

    return [handleClearChat];
};
